/**
 * Review Tab
 *
 * Performance Lab Pro™ Performance Review tab component.
 * Displays eligibility, generates AI reports, and shows results.
 */

import { useState, useRef, useCallback } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { usePerformanceReview, formatPeriod } from "../hooks";
import {
  ParticleTextReveal,
  FloatingParticles,
  ShimmerEffect,
} from "./particle-text-reveal";
import { AnalysisCard, EmptyState } from "./shared/analysis-card";
import type { PerformanceReview } from "@nordex/core";
import { toPng } from "html-to-image";
import {
  SparklesIcon,
  CheckCircleIcon,
  XCircleIcon,
  DownloadIcon,
  RefreshCwIcon,
  ClockIcon,
  BarChart3Icon,
  AlertTriangleIcon,
} from "lucide-react";

// Site colors
const SITE_GREEN = "#2ebd85";
const SITE_RED = "#f6465d";

/**
 * Main Review Tab Component
 */
export function ReviewTab() {
  const { userId } = useUnifiedAuth();
  const {
    eligibility,
    isEligibilityLoading,
    currentReview,
    isReviewLoading,
    generateReview,
    isGenerating,
    generateError,
    hasReport,
  } = usePerformanceReview(userId ?? undefined);

  const [isRevealing, setIsRevealing] = useState(false);
  const [revealedSections, setRevealedSections] = useState<Set<number>>(
    new Set()
  );
  const reportRef = useRef<HTMLDivElement>(null);

  // Handle generate click
  const handleGenerate = useCallback(() => {
    setIsRevealing(true);
    setRevealedSections(new Set());

    // If report already exists, force regeneration; otherwise generate normally
    generateReview(hasReport, {
      onSuccess: () => {
        // Start revealing sections one by one (4 sections: summary, strengths, improve, recommendations)
        const sections = [0, 1, 2, 3];
        sections.forEach((section, index) => {
          setTimeout(() => {
            setRevealedSections((prev) => new Set([...prev, section]));
          }, index * 800);
        });

        // Complete reveal
        setTimeout(() => {
          setIsRevealing(false);
        }, sections.length * 800 + 1000);
      },
      onError: () => {
        setIsRevealing(false);
      },
    });
  }, [generateReview, hasReport]);

  // Handle PDF download
  const handleDownloadPdf = useCallback(async () => {
    if (!reportRef.current) return;

    try {
      const dataUrl = await toPng(reportRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: "#0a0a0b",
      });

      const link = document.createElement("a");
      link.download = `performance-lab-review-${
        currentReview?.period_month
      }-${currentReview?.period_year}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    }
  }, [currentReview]);

  // Loading state
  if (isEligibilityLoading || isReviewLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCwIcon className="size-8 text-muted-foreground animate-spin" />
          <p className="text-sm text-muted-foreground">
            Loading Performance Review...
          </p>
        </div>
      </div>
    );
  }

  // No user
  if (!userId) {
    return (
      <EmptyState
        icon={<BarChart3Icon className="size-8" />}
        title="Sign In Required"
        description="Please sign in to access your Performance Review."
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <EligibilityCard
        eligibility={eligibility}
        hasReport={hasReport}
        currentReview={currentReview}
        isGenerating={isGenerating}
        onGenerate={handleGenerate}
      />

      {/* Generation Animation */}
      {isGenerating && (
        <GeneratingCard />
      )}

      {/* Error Display */}
      {generateError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangleIcon className="size-5" />
            <span className="font-medium">Generation Failed</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {generateError instanceof Error
              ? generateError.message
              : "An error occurred while generating your review."}
          </p>
        </div>
      )}

      {/* Report Display */}
      {currentReview && !isGenerating && (
        <div ref={reportRef}>
          <ReportDisplay
            review={currentReview}
            isRevealing={isRevealing}
            revealedSections={revealedSections}
          />
        </div>
      )}

      {/* Download Button */}
      {currentReview && !isGenerating && !isRevealing && (
        <div className="flex justify-center gap-3">
          <button
            onClick={handleDownloadPdf}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium 
                       bg-primary text-primary-foreground rounded-lg 
                       hover:bg-primary/90 transition-colors"
          >
            <DownloadIcon className="size-4" />
            Download Report
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Eligibility Card
 */
interface EligibilityCardProps {
  eligibility: {
    eligible: boolean;
    reason: string;
    trades_30d: number;
    fees_30d: number;
    volume_30d: number;
    current_month: number;
    current_year: number;
    has_report_this_month: boolean;
    last_report_date: string | null;
  } | undefined;
  hasReport: boolean;
  currentReview: PerformanceReview | null | undefined;
  isGenerating: boolean;
  onGenerate: () => void;
}

function EligibilityCard({
  eligibility,
  hasReport,
  currentReview: _currentReview,
  isGenerating,
  onGenerate,
}: EligibilityCardProps) {
  if (!eligibility) return null;

  const canGenerate = eligibility.eligible && !isGenerating;

  return (
    <AnalysisCard
      title={
        <span className="flex items-center gap-2">
          Performance Lab Pro™ Review
          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary/20 text-primary rounded">
            Beta
          </span>
        </span>
      }
      subtitle={formatPeriod(eligibility.current_month, eligibility.current_year)}
      action={
        eligibility.eligible ? (
          <span className="flex items-center gap-1 text-xs text-bid-foreground">
            <CheckCircleIcon className="size-3" />
            Eligible
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-ask-foreground">
            <XCircleIcon className="size-3" />
            Not Eligible
          </span>
        )
      }
    >
      <div className="space-y-4">
        {/* Explanation */}
        <p className="text-xs text-muted-foreground">
          Your review analyzes your Performance Lab Pro™ trading statistics to highlight what you're doing well, areas to focus on, and personalized recommendations.
        </p>

        {/* Eligibility metrics - only trades and volume */}
        <div className="grid grid-cols-2 gap-3">
          <EligibilityMetric
            label="Trades (30d)"
            value={eligibility.trades_30d.toString()}
            target="50"
            isMet={eligibility.trades_30d >= 50}
          />
          <EligibilityMetric
            label="Volume (30d)"
            value={`$${formatCurrency(eligibility.volume_30d, 0)}`}
            target="$100k"
            isMet={eligibility.volume_30d >= 100000}
          />
        </div>

        {/* Last report info */}
        {eligibility.last_report_date && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ClockIcon className="size-3" />
            Last generated:{" "}
            {new Date(eligibility.last_report_date).toLocaleDateString()}
          </div>
        )}

        {/* Generate button */}
        <div className="flex justify-center pt-2">
          <button
            onClick={onGenerate}
            disabled={!canGenerate}
            className={cn(
              "flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-lg transition-all",
              canGenerate
                ? "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02]"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            )}
          >
            <SparklesIcon className="size-4" />
            {hasReport ? "Regenerate Review" : "Generate Review"}
          </button>
        </div>

        {/* Info note */}
        <p className="text-[10px] text-center text-muted-foreground">
          {hasReport
            ? "You can regenerate to get an updated analysis."
            : "Your review will analyze the last 30 days of trading activity."}
        </p>
      </div>
    </AnalysisCard>
  );
}

/**
 * Eligibility Metric Display
 */
interface EligibilityMetricProps {
  label: string;
  value: string;
  target: string;
  isMet: boolean;
}

function EligibilityMetric({ label, value, target, isMet }: EligibilityMetricProps) {
  return (
    <div className="text-center p-2 rounded-lg bg-muted/20">
      <div className="text-[10px] text-muted-foreground mb-1">{label}</div>
      <div
        className={cn(
          "text-lg font-bold",
          isMet ? "text-bid-foreground" : "text-foreground"
        )}
      >
        {value}
      </div>
      <div className="text-[9px] text-muted-foreground">
        Need: {target}
      </div>
    </div>
  );
}

/**
 * Generating Animation Card
 */
function GeneratingCard() {
  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-card p-8">
      {/* Floating particles background */}
      <FloatingParticles count={40} />

      {/* Shimmer effect */}
      <ShimmerEffect />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center gap-4">
        <div className="relative">
          <SparklesIcon className="size-12 text-primary animate-pulse" />
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(circle, ${SITE_GREEN}40 0%, transparent 70%)`,
              animation: "glow-pulse 2s ease-in-out infinite",
            }}
          />
        </div>

        <div className="text-center">
          <h3 className="text-lg font-bold text-foreground mb-2">
            Generating Your Performance Review
          </h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Our AI is analyzing your trading patterns, identifying strengths,
            and preparing personalized recommendations...
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="size-2 rounded-full bg-primary"
              style={{
                animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Report Display Component
 */
interface ReportDisplayProps {
  review: PerformanceReview;
  isRevealing: boolean;
  revealedSections: Set<number>;
}

function ReportDisplay({
  review,
  isRevealing,
  revealedSections,
}: ReportDisplayProps) {
  const { ai_report, metrics_snapshot, is_first_report } = review;
  
  // Support both old field name (key_weaknesses) and new field name (areas_to_improve)
  const areasToImprove = (ai_report as any).areas_to_improve || (ai_report as any).key_weaknesses || [];

  return (
    <div className="space-y-4">
      {/* Executive Summary */}
      <ParticleTextReveal
        isRevealing={isRevealing && !revealedSections.has(0)}
        delay={0}
        showParticles={!revealedSections.has(0)}
      >
        <ReportSection
          title={is_first_report ? "Executive Summary" : "Performance Progress"}
          accentColor="#a855f7"
        >
          <p className="text-sm text-muted-foreground leading-relaxed">
            {ai_report.executive_summary}
          </p>
        </ReportSection>
      </ParticleTextReveal>

      {/* Key Strengths */}
      <ParticleTextReveal
        isRevealing={isRevealing && !revealedSections.has(1)}
        delay={0.2}
        showParticles={!revealedSections.has(1)}
        glowColor={SITE_GREEN}
      >
        <ReportSection
          title="What You're Doing Well"
          accentColor={SITE_GREEN}
        >
          <ul className="space-y-2 list-disc list-inside">
            {ai_report.key_strengths.map((strength, i) => (
              <li key={i} className="text-sm text-muted-foreground">
                {strength}
              </li>
            ))}
          </ul>
        </ReportSection>
      </ParticleTextReveal>

      {/* Areas to Improve */}
      <ParticleTextReveal
        isRevealing={isRevealing && !revealedSections.has(2)}
        delay={0.4}
        showParticles={!revealedSections.has(2)}
        glowColor={SITE_RED}
      >
        <ReportSection
          title="Areas to Focus On"
          accentColor={SITE_RED}
        >
          <ul className="space-y-2 list-disc list-inside">
            {areasToImprove.map((item: string, i: number) => (
              <li key={i} className="text-sm text-muted-foreground">
                {item}
              </li>
            ))}
          </ul>
        </ReportSection>
      </ParticleTextReveal>

      {/* Recommendations */}
      <ParticleTextReveal
        isRevealing={isRevealing && !revealedSections.has(3)}
        delay={0.6}
        showParticles={!revealedSections.has(3)}
        glowColor="#f59e0b"
      >
        <ReportSection
          title="Recommendations"
          accentColor="#f59e0b"
        >
          <ol className="space-y-3">
            {ai_report.recommendations.map((rec, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm text-muted-foreground"
              >
                <span className="flex items-center justify-center size-5 rounded-full bg-amber-500/20 text-amber-500 text-xs font-bold flex-shrink-0">
                  {i + 1}
                </span>
                <span>{rec}</span>
              </li>
            ))}
          </ol>
        </ReportSection>
      </ParticleTextReveal>

      {/* Comparison sections (for non-first reports) */}
      {!is_first_report && ai_report.improvements && ai_report.improvements.length > 0 && (
        <ReportSection
          title="Improvements Since Last Month"
          accentColor={SITE_GREEN}
        >
          <ul className="space-y-2 list-disc list-inside">
            {ai_report.improvements.map((item, i) => (
              <li key={i} className="text-sm text-muted-foreground">
                {item}
              </li>
            ))}
          </ul>
        </ReportSection>
      )}

      {!is_first_report && ai_report.regressions && ai_report.regressions.length > 0 && (
        <ReportSection
          title="Needs Attention"
          accentColor={SITE_RED}
        >
          <ul className="space-y-2 list-disc list-inside">
            {ai_report.regressions.map((item, i) => (
              <li key={i} className="text-sm text-muted-foreground">
                {item}
              </li>
            ))}
          </ul>
        </ReportSection>
      )}

      {/* Metrics Summary */}
      <MetricsSummary metrics={metrics_snapshot} />

      {/* Disclaimer & Watermark */}
      <div className="text-center py-4 space-y-2">
        <p className="text-[10px] text-muted-foreground/70">
          This analysis is for educational purposes only and does not constitute financial advice.
        </p>
        <p className="text-[10px] text-muted-foreground/50">
          Performance Lab Pro™ Review • Generated{" "}
          {new Date(review.created_at).toLocaleDateString()} • nordex.trade
        </p>
      </div>
    </div>
  );
}

/**
 * Report Section Card
 */
interface ReportSectionProps {
  title: string;
  accentColor: string;
  children: React.ReactNode;
}

function ReportSection({ title, accentColor, children }: ReportSectionProps) {
  return (
    <div
      className="relative rounded-xl border border-border bg-card overflow-hidden"
      style={{
        borderLeftColor: accentColor,
        borderLeftWidth: 3,
      }}
    >
      <div className="p-4">
        <h3 className="font-bold text-foreground mb-3">{title}</h3>
        {children}
      </div>
    </div>
  );
}

/**
 * Metrics Summary Card
 */
interface MetricsSummaryProps {
  metrics: PerformanceReview["metrics_snapshot"];
}

function MetricsSummary({ metrics }: MetricsSummaryProps) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
        <BarChart3Icon className="size-5 text-muted-foreground" />
        Performance Metrics Snapshot
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Net P&L - moved to first position */}
        <MetricItem
          label="Net P&L"
          value={`$${formatCurrency(Math.abs(metrics.total_net_pnl), 2)}`}
          isPositive={metrics.total_net_pnl >= 0}
          prefix={metrics.total_net_pnl >= 0 ? "+" : "-"}
        />
        <MetricItem
          label="Total Trades"
          value={metrics.total_trades.toString()}
        />
        {/* Win Rate - always white text (no isPositive) */}
        <MetricItem
          label="Win Rate"
          value={`${metrics.win_rate.toFixed(1)}%`}
        />
        {/* Profit Factor - always white text (no isPositive) */}
        <MetricItem
          label="Profit Factor"
          value={metrics.profit_factor.toFixed(2)}
        />
        <MetricItem
          label="Avg Win"
          value={`$${formatCurrency(metrics.average_profit, 2)}`}
          isPositive
        />
        <MetricItem
          label="Avg Loss"
          value={`$${formatCurrency(metrics.average_loss, 2)}`}
          isPositive={false}
        />
        <MetricItem
          label="Max Drawdown"
          value={`${metrics.max_drawdown_percent.toFixed(1)}%`}
        />
        <MetricItem
          label="Expectancy"
          value={`$${formatCurrency(Math.abs(metrics.expectancy), 2)}`}
          isPositive={metrics.expectancy >= 0}
          prefix={metrics.expectancy >= 0 ? "+" : "-"}
        />
      </div>
    </div>
  );
}

/**
 * Metric Item
 */
interface MetricItemProps {
  label: string;
  value: string;
  isPositive?: boolean;
  prefix?: string;
}

function MetricItem({ label, value, isPositive, prefix }: MetricItemProps) {
  return (
    <div className="text-center p-2 rounded-lg bg-muted/20">
      <div className="text-[10px] text-muted-foreground mb-1">{label}</div>
      <div
        className={cn(
          "text-sm font-bold",
          isPositive === true && "text-bid-foreground",
          isPositive === false && "text-ask-foreground",
          isPositive === undefined && "text-foreground"
        )}
      >
        {prefix}
        {value}
      </div>
    </div>
  );
}

